import * as v from 'valibot';

// src/marketplace/marketplace.ts

// src/api/config.ts
var Api = {
  config: {
    prefix: "/api/codex/v1"
  }
};
var CodexError = class extends Error {
  code;
  errors;
  sourceStack;
  constructor(message, { code, errors, sourceStack } = {}) {
    super(message);
    this.code = code || null;
    this.errors = errors || null;
    this.sourceStack = sourceStack || null;
  }
};
var CodexValibotIssuesMap = (issues) => issues.map((i) => {
  var _a;
  return {
    expected: i.expected,
    received: i.received,
    message: i.message,
    path: (_a = i.path) == null ? void 0 : _a.map((item) => item.key).join(".")
  };
});
var CodexCreateAvailabilityInput = v.strictObject({
  totalSize: v.pipe(v.number(), v.minValue(1)),
  duration: v.pipe(v.number(), v.minValue(1)),
  minPricePerBytePerSecond: v.number(),
  totalCollateral: v.number()
});
var CodexUpdateAvailabilityInput = v.strictObject({
  id: v.string(),
  totalSize: v.pipe(v.number(), v.minValue(1)),
  duration: v.pipe(v.number(), v.minValue(1)),
  minPricePerBytePerSecond: v.number(),
  totalCollateral: v.number()
});
var CodexCreateStorageRequestInput = v.strictObject({
  cid: v.string(),
  duration: v.pipe(v.number(), v.minValue(1)),
  pricePerBytePerSecond: v.number(),
  proofProbability: v.number(),
  nodes: v.optional(v.number(), 1),
  tolerance: v.optional(v.number(), 0),
  expiry: v.pipe(v.number(), v.minValue(1)),
  collateralPerByte: v.number()
});
var CodexLogLevel = v.picklist([
  "TRACE",
  "DEBUG",
  "INFO",
  "NOTICE",
  "WARN",
  "ERROR",
  "FATAL"
]);
var CodexDebug = class {
  url;
  constructor(url) {
    this.url = url;
  }
  /**
   * Set log level at run time
   */
  async setLogLevel(level) {
    const result = v.safeParse(CodexLogLevel, level);
    if (!result.success) {
      return Promise.resolve({
        error: true,
        data: new CodexError("Cannot validate the input", {
          errors: CodexValibotIssuesMap(result.issues)
        })
      });
    }
    const url = this.url + Api.config.prefix + "/debug/chronicles/loglevel?level=" + level;
    const res = await Fetch.safe(url, {
      method: "POST",
      body: ""
    });
    if (res.error) {
      return res;
    }
    return { error: false, data: "" };
  }
  /**
   * Gets node information
   */
  info() {
    const url = this.url + Api.config.prefix + `/debug/info`;
    return Fetch.safeJson(url, {
      method: "GET"
    });
  }
};

// src/data/data.ts
var CodexData = class {
  url;
  constructor(url) {
    this.url = url;
  }
  /**
   * Lists manifest CIDs stored locally in node.
   * TODO: remove the faker data part when the api is ready
   */
  cids() {
    const url = this.url + Api.config.prefix + "/data";
    return Fetch.safeJson(url, {
      method: "GET"
    }).then((data) => {
      if (data.error) {
        return data;
      }
      return {
        error: false,
        data: {
          content: data.data.content
        }
      };
    });
  }
  /**
   * Gets a summary of the storage space allocation of the node.
   */
  space() {
    const url = this.url + Api.config.prefix + "/space";
    return Fetch.safeJson(url, {
      method: "GET"
    });
  }
  /**
   * Upload a file in a streaming manner.
   * Once completed, the file is stored in the node and can be retrieved by any node in the network using the returned CID.
   * XMLHttpRequest is used instead of fetch for this case, to obtain progress information.
   * A callback onProgress can be passed to receive upload progress data information.
   */
  upload(file, onProgress, metadata = {}) {
    const url = this.url + Api.config.prefix + "/data";
    const xhr = new XMLHttpRequest();
    const promise = new Promise((resolve) => {
      xhr.upload.onprogress = (evt) => {
        if (evt.lengthComputable) {
          onProgress == null ? void 0 : onProgress(evt.loaded, evt.total);
        }
      };
      xhr.open("POST", url, true);
      if (metadata.filename) {
        xhr.setRequestHeader("Content-Disposition", 'attachment; filename="' + metadata.filename + '"');
      }
      if (metadata.mimetype) {
        xhr.setRequestHeader("Content-Type", metadata.mimetype);
      }
      xhr.send(file);
      xhr.onload = function() {
        if (xhr.status != 200) {
          resolve({
            error: true,
            data: new CodexError(xhr.responseText, {
              code: xhr.status
            })
          });
        } else {
          resolve({ error: false, data: xhr.response });
        }
      };
      xhr.onerror = function() {
        resolve({
          error: true,
          data: new CodexError("Something went wrong during the file upload.")
        });
      };
    });
    return {
      result: promise,
      abort: () => {
        xhr.abort();
      }
    };
  }
  /**
   * Download a file from the local node in a streaming manner.
   * If the file is not available locally, a 404 is returned.
   */
  async localDownload(cid) {
    const url = this.url + Api.config.prefix + "/data/" + cid;
    return Fetch.safe(url, {
      method: "GET"
    });
  }
  /**
   * Download a file from the network in a streaming manner.
   * If the file is not available locally, it will be retrieved from other nodes in the network if able.
   */
  async networkDownload(cid) {
    const url = this.url + Api.config.prefix + `/data/${cid}/network`;
    return Fetch.safeJson(url, {
      method: "POST"
    });
  }
  /**
   * Download a file from the network in a streaming manner. 
   * If the file is not available locally, it will be retrieved from other nodes in the network if able.
   */
  async networkDownloadStream(cid) {
    const url = this.url + Api.config.prefix + `/data/${cid}/network/stream`;
    return Fetch.safe(url, {
      method: "GET"
    });
  }
  /**
    * Download only the dataset manifest from the network to the local node 
    * if it's not available locally.
   */
  async fetchManifest(cid) {
    const url = this.url + Api.config.prefix + `/data/${cid}/network/manifest`;
    return Fetch.safeJson(url, {
      method: "GET"
    });
  }
};

// src/node/node.ts
var CodexNode = class {
  url;
  constructor(url) {
    this.url = url;
  }
  /**
   * Connect to a peer
   * TODO check result
   */
  connect(peerId, addrs = []) {
    const params = new URLSearchParams();
    for (const addr of addrs) {
      params.append("addrs", addr);
    }
    const url = this.url + Api.config.prefix + `/connect/${peerId}?` + params.toString();
    return Fetch.safe(url, {
      method: "GET"
    });
  }
  /**
   * Get Node's SPR
   */
  async spr() {
    const url = this.url + Api.config.prefix + "/spr";
    return Fetch.safeJson(url, {
      method: "GET"
    });
  }
  /**
   * Get Node's PeerID
   * TODO check result
   */
  peerId() {
    const url = this.url + Api.config.prefix + "/node/peerid";
    return Fetch.safe(url, {
      method: "GET"
    });
  }
};

// src/async.ts
var Codex = class {
  url;
  _marketplace;
  _data;
  _node;
  _debug;
  constructor(url) {
    this.url = url;
    this._marketplace = null;
    this._data = null;
    this._node = null;
    this._debug = null;
  }
  async marketplace() {
    if (this._marketplace) {
      return this._marketplace;
    }
    const module = await import('./marketplace-FR3564QJ.mjs');
    this._marketplace = new module.CodexMarketplace(this.url);
    return this._marketplace;
  }
  async data() {
    if (this._data) {
      return this._data;
    }
    const module = await import('./data-T2M2K4EE.mjs');
    this._data = new module.CodexData(this.url);
    return this._data;
  }
  async node() {
    if (this._node) {
      return this._node;
    }
    const module = await import('./node-DT4JMZIO.mjs');
    this._node = new module.CodexNode(this.url);
    return this._node;
  }
  async debug() {
    if (this._debug) {
      return this._debug;
    }
    const module = await import('./debug-IWL6FAB3.mjs');
    this._debug = new module.CodexDebug(this.url);
    return this._debug;
  }
};

// src/promise-safe/promise-safe.ts
var Promises = {
  async safe(promise) {
    try {
      const result = await promise();
      return { error: false, data: result };
    } catch (e) {
      return {
        error: true,
        data: new CodexError(e instanceof Error ? e.message : "" + e, {
          sourceStack: e instanceof Error ? e.stack || null : null
        })
      };
    }
  }
};

// src/fetch-safe/fetch-safe.ts
var Fetch = {
  async safe(url, init) {
    const res = await Promises.safe(() => fetch(url, init));
    if (res.error) {
      return {
        error: true,
        data: new CodexError(res.data.message, {
          code: 502
        })
      };
    }
    if (!res.data.ok) {
      const message = await Promises.safe(() => res.data.text());
      if (message.error) {
        return message;
      }
      return {
        error: true,
        data: new CodexError(message.data, {
          code: res.data.status
        })
      };
    }
    return { error: false, data: res.data };
  },
  async safeJson(url, init) {
    const res = await this.safe(url, init);
    if (res.error) {
      return res;
    }
    return Promises.safe(() => res.data.json());
  }
};

// src/marketplace/marketplace.ts
var CodexMarketplace = class {
  url;
  constructor(url) {
    this.url = url;
  }
  /**
   * Returns active slots
   */
  async activeSlots() {
    const url = this.url + Api.config.prefix + "/sales/slots";
    return Fetch.safeJson(url, {
      method: "GET"
    });
  }
  /**
   * Returns active slot with id {slotId} for the host
   */
  async activeSlot(slotId) {
    const url = this.url + Api.config.prefix + "/sales/slots/" + slotId;
    return Fetch.safeJson(url, {
      method: "GET"
    });
  }
  /**
   * Returns storage that is for sale
   */
  async availabilities() {
    const url = this.url + Api.config.prefix + "/sales/availability";
    const res = await Fetch.safeJson(url, {
      method: "GET"
    });
    if (res.error) {
      return res;
    }
    return {
      error: false,
      data: res.data.map((a) => ({
        id: a.id,
        totalSize: parseInt(a.totalSize, 10),
        freeSize: parseInt(a.freeSize, 10),
        duration: parseInt(a.duration, 10),
        minPricePerBytePerSecond: parseInt(a.minPricePerBytePerSecond, 10),
        totalCollateral: parseInt(a.totalCollateral, 10),
        totalRemainingCollateral: parseInt(a.totalRemainingCollateral, 10)
      }))
    };
  }
  /**
   * Offers storage for sale
   */
  async createAvailability(input) {
    const result = v.safeParse(CodexCreateAvailabilityInput, input);
    if (!result.success) {
      return {
        error: true,
        data: new CodexError("Cannot validate the input", {
          errors: CodexValibotIssuesMap(result.issues)
        })
      };
    }
    const url = this.url + Api.config.prefix + "/sales/availability";
    const body = result.output;
    return Fetch.safeJson(url, {
      method: "POST",
      body: JSON.stringify({
        totalSize: body.totalSize.toString(),
        duration: body.duration.toString(),
        minPricePerBytePerSecond: body.minPricePerBytePerSecond.toString(),
        totalCollateral: body.totalCollateral.toString()
      })
    });
  }
  /**
   * The new parameters will be only considered for new requests.
   * Existing Requests linked to this Availability will continue as is.
   */
  async updateAvailability(input) {
    const result = v.safeParse(CodexUpdateAvailabilityInput, input);
    if (!result.success) {
      return {
        error: true,
        data: new CodexError("Cannot validate the input", {
          errors: CodexValibotIssuesMap(result.issues)
        })
      };
    }
    const url = this.url + Api.config.prefix + "/sales/availability/" + result.output.id;
    const body = result.output;
    const res = await Fetch.safe(url, {
      method: "PATCH",
      body: JSON.stringify({
        totalSize: body.totalSize.toString(),
        duration: body.duration.toString(),
        minPricePerBytePerSecond: body.minPricePerBytePerSecond.toString(),
        totalCollateral: body.totalCollateral.toString()
      })
    });
    if (res.error) {
      return res;
    }
    return { error: false, data: "" };
  }
  /**
   * Return's list of Reservations for ongoing Storage Requests that the node hosts.
   */
  async reservations(availabilityId) {
    const url = this.url + Api.config.prefix + `/sales/availability/${availabilityId}/reservations`;
    return Fetch.safeJson(url, {
      method: "GET"
    });
  }
  /**
   * Returns list of purchase IDs
   */
  async purchaseIds() {
    const url = this.url + Api.config.prefix + `/storage/purchases`;
    return Fetch.safeJson(url, {
      method: "GET"
    });
  }
  async purchases() {
    const url = this.url + Api.config.prefix + `/storage/purchases`;
    const res = await Fetch.safeJson(url, {
      method: "GET"
    });
    if (res.error) {
      return res;
    }
    const promises = [];
    for (const id of res.data) {
      promises.push(this.purchaseDetail(id));
    }
    const purchases = await Promise.all(promises);
    return {
      error: false,
      data: purchases.map(
        (p) => p.error ? {
          state: "error",
          error: p.data.message,
          requestId: "",
          request: {}
        } : p.data
      )
    };
  }
  /**
   * Returns purchase details
   */
  async purchaseDetail(purchaseId) {
    const url = this.url + Api.config.prefix + `/storage/purchases/` + purchaseId;
    return Fetch.safeJson(url, {
      method: "GET"
    });
  }
  /**
   * Creates a new request for storage.
   */
  async createStorageRequest(input) {
    const result = v.safeParse(CodexCreateStorageRequestInput, input);
    if (!result.success) {
      return {
        error: true,
        data: new CodexError("Cannot validate the input", {
          errors: CodexValibotIssuesMap(result.issues)
        })
      };
    }
    const {
      cid,
      duration,
      pricePerBytePerSecond,
      proofProbability,
      nodes,
      collateralPerByte,
      expiry,
      tolerance
    } = result.output;
    const url = this.url + Api.config.prefix + "/storage/request/" + cid;
    const res = await Fetch.safe(url, {
      method: "POST",
      body: JSON.stringify({
        duration: duration.toString(),
        pricePerBytePerSecond: pricePerBytePerSecond.toString(),
        proofProbability: proofProbability.toString(),
        nodes,
        collateralPerByte: collateralPerByte.toString(),
        expiry: expiry.toString(),
        tolerance
      })
    });
    if (res.error) {
      return res;
    }
    return { error: false, data: await res.data.text() };
  }
};

export { Codex, CodexCreateAvailabilityInput, CodexCreateStorageRequestInput, CodexData, CodexDebug, CodexError, CodexLogLevel, CodexMarketplace, CodexNode, CodexUpdateAvailabilityInput, CodexValibotIssuesMap, Fetch };
//# sourceMappingURL=chunk-B23RMVB7.mjs.map
//# sourceMappingURL=chunk-B23RMVB7.mjs.map